import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UserState } from "./PayloadTypes/AuthPayload";
import { User } from "../../types/common.types";
import { authStorage } from "../../auth/authStorage";
import { AuthError, AuthStatus } from "../../auth/authTypes";

export enum USER_STATE {
  LOGGED_OUT,
  LOGGED_IN,
}
export interface AuthState {
  userState: USER_STATE;
  user: User;
  isAuthenticated: boolean;
  status: AuthStatus;
  error: AuthError | null;
}
const storedSession = authStorage.getSession();
const storedToken = authStorage.getToken();

export const emptyUser: User = {
  currency: { prefix: "", name: "" },
  defaultLanguage: "",
  canPlaceOrder: false,
  orderPaymentPolicy: "pay_after_session",
  paymentExperience: "hosted_checkout",
  legalName: "",
  languages: [],
  segments: [],
  hasSegments: false,
  username: "",
  userLanguages: [],
  activeLanguage: "",
  address: "",
  brief: "",
  contacts: [],
  activities: [],
  banner: "",
  logo: "",
  membershipDetails: {
    id: "",
    name: "",
    key: "basic",
    price: 0,
    description: [],
    limits: { staff: 0, product: 0, segment: 0, order: false },
  },
  isVerified: false,
  passwordResetRequired: false,
};

const initialState: AuthState = {
  userState: 0,
  user: storedSession?.user || emptyUser,
  isAuthenticated: false,
  status: storedToken ? "initializing" : "anonymous",
  error: null,
};

export const AuthSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUserState: (state, action: PayloadAction<UserState>) => {
      return {
        ...state,
        userState: action.payload.state,
        user: action.payload.user,
        isAuthenticated: action.payload.state === USER_STATE.LOGGED_IN,
        status:
          action.payload.state === USER_STATE.LOGGED_IN
            ? action.payload.user.isVerified
              ? "authenticated"
              : "verification-required"
            : "anonymous",
        error: null,
      };
    },
    authStarted: (state) => {
      state.status = "authenticating";
      state.error = null;
    },
    bootstrapStarted: (state) => {
      state.status = "initializing";
      state.error = null;
    },
    bootstrapFailed: (state, action: PayloadAction<AuthError>) => {
      state.status = "bootstrap-failed";
      state.isAuthenticated = false;
      state.error = action.payload;
    },
    authSucceeded: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.userState = USER_STATE.LOGGED_IN;
      state.isAuthenticated = true;
      state.status = action.payload.isVerified
        ? "authenticated"
        : "verification-required";
      state.error = null;
    },
    authFailed: (state, action: PayloadAction<AuthError>) => {
      state.userState = USER_STATE.LOGGED_OUT;
      state.isAuthenticated = false;
      state.status = "anonymous";
      state.error = action.payload;
    },
    sessionExpired: (state) => {
      state.userState = USER_STATE.LOGGED_OUT;
      state.user = emptyUser;
      state.isAuthenticated = false;
      state.status = "session-expired";
      state.error = null;
    },
    loggedOut: (state) => {
      state.userState = USER_STATE.LOGGED_OUT;
      state.user = emptyUser;
      state.isAuthenticated = false;
      state.status = "anonymous";
      state.error = null;
    },
    updateActiveLanguage: (state, action: PayloadAction<string>) => {
      state.user.activeLanguage = action.payload;

      return state;
    },
    updateIsAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
      if (!action.payload) state.status = "anonymous";
    },

    updateIsVerified: (state, action: PayloadAction<boolean>) => {
      state.user.isVerified = action.payload;
      state.status = action.payload ? "authenticated" : "verification-required";
    },
  },
});

export const {
  setUserState,
  updateActiveLanguage,
  updateIsAuthenticated,
  updateIsVerified,
  authStarted,
  bootstrapStarted,
  bootstrapFailed,
  authSucceeded,
  authFailed,
  sessionExpired,
  loggedOut,
} = AuthSlice.actions;

export default AuthSlice.reducer;
